import AdminMembersReportPathwayProgress from "@/admin/insights/member-reports/tabs/products/AdminMembersReportPathwayProgress"
import { AdminMembersReportProductsTableRowFragment$key } from "@/admin/insights/member-reports/tabs/products/__generated__/AdminMembersReportProductsTableRowFragment.graphql"
import { generateDateText } from "@/organization/people/list/util/organizationPeopleListUtils"
import ProductWithDetails from "@/product/common/ProductWithDetails"
import MemberCurriculumProgressBar from "@/product/reports/page/curriculum/MemberCurriculumProgressBar"
import EventAttendanceProgressBar from "@/product/reports/page/event-attendance/EventAttendanceProgressBar"
import useUserTimezone from "@/user/util/useUserTimezone"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoTable } from "@disco-ui"
import { TableCell } from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import { range } from "@utils/array/arrayUtils"
import DateUtils from "@utils/date/dateUtils"
import { DATE_FORMAT } from "@utils/time/timeConstants"
import { TestIDProps } from "@utils/typeUtils"
import { formatInTimeZone } from "date-fns-tz"
import { graphql, useFragment } from "react-relay"

interface Props extends TestIDProps {
  productMembershipKey: AdminMembersReportProductsTableRowFragment$key
}

function AdminMembersReportProductsTableRow({
  testid = "AdminMembersReportProductsTableRow",
  productMembershipKey,
}: Props) {
  const authUserTimezone = useUserTimezone()
  const classes = useStyles()
  const productMembership = useFragment<AdminMembersReportProductsTableRowFragment$key>(
    graphql`
      fragment AdminMembersReportProductsTableRowFragment on ProductMembership {
        id
        product {
          type
          curriculumId
          ...ProductWithDetailsFragment
        }
        creationDatetime
        ...MemberCurriculumProgressBarFragment
        ...EventAttendanceProgressBarFragment
        engagementScoreReport {
          allTime
        }
        lastActivityAt
        timeSpentSeconds
        ...AdminMembersReportPathwayProgressFragment
      }
    `,
    productMembershipKey
  )

  const { product } = productMembership
  const productType = product.type

  return (
    <>
      <DiscoTable.Row testid={testid}>
        {/* Product */}
        <TableCell className={classes.productColumn}>
          <ProductWithDetails productKey={product} />
        </TableCell>

        {/* Enrollment Date */}
        <TableCell>
          {formatInTimeZone(
            productMembership.creationDatetime,
            authUserTimezone,
            DATE_FORMAT.DEFAULT
          )}
        </TableCell>

        {productType === "pathway" ? (
          // Progress
          <TableCell>
            <AdminMembersReportPathwayProgress memberKey={productMembership} />
          </TableCell>
        ) : (
          <>
            {/* Curriculum */}
            <TableCell>
              {product.curriculumId ? (
                <MemberCurriculumProgressBar memberKey={productMembership} />
              ) : (
                "-"
              )}
            </TableCell>

            {/* Event Attendance */}
            <TableCell>
              <EventAttendanceProgressBar memberKey={productMembership} />
            </TableCell>
          </>
        )}

        {/* Engagement Score */}
        <TableCell>{productMembership.engagementScoreReport?.allTime || 0}</TableCell>

        {/* Last Active */}
        <TableCell>
          {generateDateText(authUserTimezone, productMembership.lastActivityAt) || "-"}
        </TableCell>

        {/* Time Spent */}
        <TableCell>
          {productMembership.timeSpentSeconds
            ? DateUtils.formatSecondsToHMS(productMembership.timeSpentSeconds, {
                spaceOut: true,
              })
            : "-"}
        </TableCell>
      </DiscoTable.Row>
    </>
  )
}

const useStyles = makeUseStyles({
  productColumn: {
    minWidth: "300px",
  },
})

type SkeletonProps = {
  cells: number
}
export function AdminMembersReportProductsTableRowSkeleton({ cells }: SkeletonProps) {
  return (
    <DiscoTable.Row>
      {range(cells).map((i) => (
        <TableCell key={i}>
          <Skeleton variant={"rect"} />
        </TableCell>
      ))}
    </DiscoTable.Row>
  )
}

export default AdminMembersReportProductsTableRow
